import { useParams } from "react-router-dom";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { AppointmentEdit } from "../components/AppointmentEdit";
import { useAppointment, useUpdateAppointment } from "../hooks/appointmentQueries";
import { useCalendarEventOperation } from "@/features/calendar-events/hooks/useCalendarEventOperation";

export function AppointmentEditPage() {
  const { appointmentId = "" } = useParams<{ appointmentId: string }>();
  const { businessId } = useCurrentBusiness();
  const { goToAppointmentDetails } = useAppNavigation();
  const query = useAppointment(appointmentId);
  const mutation = useUpdateAppointment(appointmentId);
  const captureOperation = useCalendarEventOperation();
  const appointment = query.data;
  if (query.isLoading || query.error || !appointment || !["pending", "confirmed"].includes(appointment.status)) {
    return <Page><PageHeader title="Edit appointment" /><PageContent>{query.isLoading ? <RowSkeleton /> : query.error ? <ErrorState message={query.error.message} onRetry={() => void query.refetch()} /> : <EmptyState message={appointment ? "This appointment can no longer be edited." : "Appointment not found."} />}</PageContent></Page>;
  }
  return (
    <AppointmentEdit
      key={`${businessId}:${appointment.id}`}
      appointment={appointment}
      title="Edit appointment"
      submitButtonText="Save changes"
      isSubmitting={mutation.isPending}
      onSubmit={async (data, services, review) => {
        const isCurrent = captureOperation();
        await mutation.mutateAsync({
          locationId: data.locationId, startAt: data.startAt, endAt: data.endAt, notes: data.notes,
          clientId: appointment.billingLocked ? undefined : data.clientId,
          price: appointment.billingLocked ? undefined : data.price,
          depositAmount: appointment.billingLocked ? undefined : data.depositAmount,
          services: appointment.billingLocked ? undefined : services,
          formsReviewToken: review.formsReviewToken,
          expectedRevision: review.expectedRevision,
        });
        if (isCurrent()) goToAppointmentDetails(appointment.id, true);
      }}
    />
  );
}
